import { useCallback, useEffect, useMemo, useState } from "react";
import {
  costRollup,
  localCalendarDate,
  rollupTotal,
  type ActivityLog,
  type ActivityType,
  type CostRollup,
  type Household,
} from "@lifelog/shared";
import { api, ApiError, isOfflineError } from "../lib/api.js";
import { formatCurrencyDollars } from "../lib/export.js";
import { CountChart } from "./report/CountChart.js";
import { PageSkeleton } from "./Skeleton.js";

const LOG_LOOKBACK_MS = 10 * 365 * 24 * 60 * 60 * 1000;

function money(minorUnits: number, currency: string): string {
  return `${formatCurrencyDollars(minorUnits)} ${currency}`;
}

function monthLabel(period: string): string {
  const date = new Date(`${period}-01T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
}

export function SpendPane({ household }: { household: Household }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [currency, setCurrency] = useState<string | null>(null);
  const [year, setYear] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = new Date(Date.now() - LOG_LOOKBACK_MS).toISOString();
      const to = new Date().toISOString();
      const [typesRes, logsRes] = await Promise.all([
        api<{ types: ActivityType[] }>("/activity-types"),
        api<{ logs: ActivityLog[] }>(
          `/logs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=500`,
        ),
      ]);
      setTypes(typesRes.types.filter((t) => !t.archived));
      setLogs(logsRes.logs);
    } catch (err) {
      if (isOfflineError(err)) setError("You're offline. Try again when you're back online.");
      else if (!(err instanceof ApiError && err.status === 401)) {
        setError(err instanceof Error ? err.message : "Couldn't load spend.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, household.householdId]);

  const groups = useMemo(
    () => costRollup(logs, types, household.timezone),
    [logs, types, household.timezone],
  );

  useEffect(() => {
    if (groups.length === 0) {
      setCurrency(null);
      return;
    }
    if (!currency || !groups.some((g) => g.currency === currency)) {
      setCurrency(groups[0]!.currency);
    }
  }, [groups, currency]);

  const group: CostRollup | undefined = groups.find((g) => g.currency === currency) ?? groups[0];
  const years = group ? group.byYear.map((r) => r.period) : [];
  const currentYear = localCalendarDate(new Date().toISOString(), household.timezone).slice(0, 4);
  const selectedYear =
    year && years.includes(year) ? year : years.includes(currentYear) ? currentYear : years[years.length - 1];
  const yearTotal = group?.byYear.find((r) => r.period === selectedYear)?.minorUnits ?? 0;
  const monthBars = useMemo(() => {
    if (!group || !selectedYear) return [];
    return group.byMonth
      .filter((r) => r.period.startsWith(`${selectedYear}-`))
      .map((r) => ({
        date: `${r.period}-01`,
        count: r.minorUnits / 100,
        label: monthLabel(r.period),
      }));
  }, [group, selectedYear]);

  if (loading) return <PageSkeleton />;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Spend</h1>
        <div className="flex flex-wrap gap-2">
          {groups.length > 1 && (
            <select
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              value={group?.currency ?? ""}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {groups.map((g) => (
                <option key={g.currency} value={g.currency}>
                  {g.currency}
                </option>
              ))}
            </select>
          )}
          {years.length > 1 && (
            <select
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              value={selectedYear ?? ""}
              onChange={(e) => setYear(e.target.value)}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {!group && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No currency fields yet. Add a cost field to an activity type to start tracking spend.
        </p>
      )}

      {group && (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {selectedYear ?? "Total"} {group.currency}
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {money(selectedYear ? yearTotal : rollupTotal(group), group.currency)}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">By month</h2>
            <CountChart
              days={monthBars}
              valueFormat={(n) => formatCurrencyDollars(Math.round(n * 100))}
              valueUnit={group.currency}
              emptyHint="No spend in this year."
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-3 text-sm font-medium text-gray-900 dark:text-gray-100">By activity</h2>
            {group.byType.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing spent.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    <th className="pb-2 font-medium">Activity</th>
                    <th className="pb-2 text-right font-medium">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {group.byType.map((row) => (
                    <tr key={row.activityTypeId} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-2 text-gray-800 dark:text-gray-200">{row.name}</td>
                      <td className="py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                        {money(row.minorUnits, group.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
